/**
 * Rules UI controller, hosted in the task pane (NOT a dialog).
 *
 * Drives the Reformat overlay: persona selector + manager, scope selector
 * + run button, draggable/orderable rule list with toggle/edit/delete,
 * and an in-overlay activity feed.
 *
 * Because this runs in the task pane, it has full Office.context.document
 * access AND can open the rule editor / persona manager sub-dialogs
 * directly (those calls only work from the task pane — not from another
 * dialog, due to Office's one-dialog-at-a-time rule).
 */
import type { Persona, Rule, Scope } from "../rules/types";
import { applyRules } from "../rules/engine";
import { getWorkbookInfo, matchPersona } from "../rules/detection";
import { loadAllRules } from "../rules/registry";
import {
  deleteUserRule,
  loadActivePersonaId,
  loadPersonas,
  loadRuleOrder,
  loadSystemRuleOverrides,
  loadUserRules,
  saveActivePersonaId,
  saveRuleOrder,
  saveSystemRuleOverrides,
  saveUserRules,
} from "../rules/storage";
import { openPersonaManager, openRuleEditor } from "../rules/dialogs";


export type RulesLogLevel = "info" | "success" | "error";

let initialized = false;
let currentRules: Rule[] = [];
let currentPersonas: Persona[] = [];
let activePersonaId: string | null = null;
let running = false;
let pendingDeleteRuleId: string | null = null;
let pendingDeleteTimer: number | null = null;

const NO_PERSONA = "";


export async function initRulesUI(): Promise<void> {
  if (initialized) {
    // Re-init: just rerender from storage and re-run auto-detection.
    await rerender();
    if (activePersonaId === null) await runAutoDetection();
    return;
  }
  initialized = true;

  document.getElementById("reformat-feed-clear")?.addEventListener("click", () => {
    const feed = document.getElementById("reformat-feed");
    if (feed) feed.replaceChildren();
  });
  document.getElementById("rules-new")?.addEventListener("click", () => {
    void handleNewRule();
  });
  document.getElementById("rules-run")?.addEventListener("click", () => {
    void handleRun();
  });
  document.getElementById("rules-persona")?.addEventListener("change", (e) => {
    const sel = e.target as HTMLSelectElement;
    void handlePersonaChange(sel.value);
  });
  document.getElementById("rules-manage-personas")?.addEventListener("click", () => {
    void handleManagePersonas();
  });

  renderLoadingPlaceholder();
  try {
    await rerender();
    if (activePersonaId === null) await runAutoDetection();
  } catch (err) {
    log(`init failed: ${errMessage(err)}`, "error");
  }
}


// ----------------------------------------------------------------------------
// In-overlay activity log
// ----------------------------------------------------------------------------

function log(message: string, level: RulesLogLevel = "info"): void {
  const feed = document.getElementById("reformat-feed");
  if (!feed) return;
  const entry = document.createElement("div");
  entry.className = `feed-entry feed-entry--${level}`;
  const time = new Date().toLocaleTimeString();
  const ts = document.createElement("span");
  ts.className = "feed-ts";
  ts.textContent = time;
  const msg = document.createElement("span");
  msg.className = "feed-msg";
  msg.textContent = message;
  entry.appendChild(ts);
  entry.appendChild(msg);
  feed.appendChild(entry);
  feed.scrollTop = feed.scrollHeight;
}


// ----------------------------------------------------------------------------
// State + rendering
// ----------------------------------------------------------------------------

function renderLoadingPlaceholder(): void {
  const list = document.getElementById("rules-list");
  if (list) {
    list.replaceChildren();
    const loading = document.createElement("p");
    loading.className = "rules-empty";
    loading.textContent = "Loading…";
    list.appendChild(loading);
  }
  const sel = document.getElementById("rules-persona") as HTMLSelectElement | null;
  if (sel) {
    sel.replaceChildren();
    const opt = document.createElement("option");
    opt.value = "";
    opt.textContent = "Loading…";
    sel.appendChild(opt);
  }
}

async function runAutoDetection(): Promise<void> {
  const candidates = currentPersonas.filter((p) => p.autoMatch !== undefined);
  if (candidates.length === 0) {
    log("auto-detect: no personas have match criteria configured");
    return;
  }
  try {
    const info = await getWorkbookInfo();
    const sheets = info.sheetNames.length > 0 ? info.sheetNames.join(", ") : "(none)";
    log(`auto-detect: workbook "${info.filename}", sheets: ${sheets}`);
    for (const persona of candidates) {
      const fn = persona.autoMatch?.filenameSubstring ?? "";
      const sh = persona.autoMatch?.sheetNameSubstring ?? "";
      const criteria = [fn && `filename⊇"${fn}"`, sh && `sheet⊇"${sh}"`]
        .filter(Boolean)
        .join(" OR ");
      const isMatch = matchPersona(persona, info);
      if (isMatch) {
        await saveActivePersonaId(persona.id);
        activePersonaId = persona.id;
        log(`auto-detect: "${persona.name}" [${criteria}] → MATCH`, "success");
        renderPersonaSelector();
        renderRulesList();
        return;
      }
      log(`auto-detect: "${persona.name}" [${criteria}] → no match`);
    }
    log("auto-detect: no persona matched this workbook");
  } catch (err) {
    log(`auto-detect failed: ${errMessage(err)}`, "error");
  }
}

async function rerender(): Promise<void> {
  try {
    const [rules, personas] = await Promise.all([loadAllRules(), loadPersonas()]);
    currentRules = rules;
    currentPersonas = personas;
    activePersonaId = loadActivePersonaId();
    if (activePersonaId && !personas.find((p) => p.id === activePersonaId)) {
      activePersonaId = null;
      await saveActivePersonaId(null);
    }
    renderPersonaSelector();
    renderRulesList();
  } catch (err) {
    log(`failed to load rules: ${errMessage(err)}`, "error");
    currentRules = [];
    currentPersonas = [];
    activePersonaId = null;
    try {
      renderPersonaSelector();
      renderRulesList();
    } catch (renderErr) {
      log(`failed to render rules: ${errMessage(renderErr)}`, "error");
    }
  }
}

function renderPersonaSelector(): void {
  const sel = document.getElementById("rules-persona") as HTMLSelectElement | null;
  if (!sel) return;
  sel.replaceChildren();

  const none = document.createElement("option");
  none.value = NO_PERSONA;
  none.textContent = "— No persona —";
  sel.appendChild(none);

  for (const persona of currentPersonas) {
    const opt = document.createElement("option");
    opt.value = persona.id;
    opt.textContent = persona.name;
    sel.appendChild(opt);
  }

  sel.value = activePersonaId ?? NO_PERSONA;
}

function activePersonaRuleIdSet(): Set<string> | null {
  if (!activePersonaId) return null;
  const persona = currentPersonas.find((p) => p.id === activePersonaId);
  if (!persona) return null;
  return new Set(persona.ruleIds);
}

function renderRulesList(): void {
  const list = document.getElementById("rules-list");
  if (!list) return;
  list.replaceChildren();

  if (currentRules.length === 0) {
    const empty = document.createElement("p");
    empty.className = "rules-empty";
    empty.textContent = "No rules yet. Click “+ New rule” to add one.";
    list.appendChild(empty);
    return;
  }

  const inPersonaIds = activePersonaRuleIdSet();
  for (let i = 0; i < currentRules.length; i++) {
    const rule = currentRules[i];
    const inPersona = inPersonaIds === null || inPersonaIds.has(rule.id);
    list.appendChild(renderRuleItem(rule, i, inPersona));
  }
}

function renderRuleItem(rule: Rule, index: number, inPersona: boolean): HTMLElement {
  const item = document.createElement("div");
  item.className = "rule-item";
  if (!inPersona) item.classList.add("not-in-persona");
  item.draggable = true;
  item.dataset.index = String(index);
  item.dataset.ruleId = rule.id;
  item.setAttribute("role", "listitem");

  const handle = document.createElement("span");
  handle.className = "rule-handle";
  handle.textContent = "≡";
  handle.title = "Drag to reorder";
  handle.setAttribute("aria-hidden", "true");
  item.appendChild(handle);

  const checkbox = document.createElement("input");
  checkbox.type = "checkbox";
  checkbox.checked = rule.enabled;
  checkbox.className = "rule-toggle";
  checkbox.title = rule.enabled ? "Disable rule" : "Enable rule";
  checkbox.setAttribute("aria-label", `Enable ${rule.name}`);
  checkbox.addEventListener("change", () => {
    void handleToggle(rule, checkbox.checked);
  });
  item.appendChild(checkbox);

  const body = document.createElement("div");
  body.className = "rule-body";
  const name = document.createElement("div");
  name.className = "rule-name";
  name.textContent = rule.name;
  body.appendChild(name);
  if (rule.description) {
    const desc = document.createElement("div");
    desc.className = "rule-desc";
    desc.textContent = rule.description;
    body.appendChild(desc);
  }
  item.appendChild(body);

  const badge = document.createElement("span");
  badge.className = `rule-source rule-source--${rule.source}`;
  badge.textContent = rule.source;
  item.appendChild(badge);

  const editBtn = document.createElement("button");
  editBtn.type = "button";
  editBtn.className = "rule-action-btn";
  editBtn.textContent = rule.source === "system" ? "View" : "Edit";
  editBtn.addEventListener("click", () => {
    void handleEdit(rule);
  });
  item.appendChild(editBtn);

  if (rule.source === "user") {
    const deleteBtn = document.createElement("button");
    deleteBtn.type = "button";
    deleteBtn.className = "rule-action-btn rule-action-btn--danger";
    deleteBtn.textContent = "Delete";
    deleteBtn.addEventListener("click", () => {
      handleDeleteClick(rule, deleteBtn);
    });
    item.appendChild(deleteBtn);
  }

  attachDragHandlers(item, index);
  return item;
}

function attachDragHandlers(item: HTMLElement, index: number): void {
  item.addEventListener("dragstart", (e) => {
    e.dataTransfer?.setData("text/plain", String(index));
    if (e.dataTransfer) e.dataTransfer.effectAllowed = "move";
    item.classList.add("dragging");
  });

  item.addEventListener("dragend", () => {
    item.classList.remove("dragging");
    clearDropIndicators();
  });

  item.addEventListener("dragover", (e) => {
    e.preventDefault();
    if (!e.dataTransfer) return;
    e.dataTransfer.dropEffect = "move";
    const rect = item.getBoundingClientRect();
    const before = e.clientY < rect.top + rect.height / 2;
    clearDropIndicators();
    item.classList.add(before ? "drop-before" : "drop-after");
  });

  item.addEventListener("dragleave", () => {
    item.classList.remove("drop-before", "drop-after");
  });

  item.addEventListener("drop", (e) => {
    e.preventDefault();
    const sourceStr = e.dataTransfer?.getData("text/plain");
    clearDropIndicators();
    if (sourceStr === undefined || sourceStr === "") return;
    const source = parseInt(sourceStr, 10);
    if (Number.isNaN(source)) return;
    const rect = item.getBoundingClientRect();
    const before = e.clientY < rect.top + rect.height / 2;
    const target = before ? index : index + 1;
    void handleReorder(source, target);
  });
}

function clearDropIndicators(): void {
  document
    .querySelectorAll(".rule-item.drop-before, .rule-item.drop-after")
    .forEach((el) => el.classList.remove("drop-before", "drop-after"));
}


// ----------------------------------------------------------------------------
// Handlers
// ----------------------------------------------------------------------------

async function handleToggle(rule: Rule, enabled: boolean): Promise<void> {
  try {
    if (rule.source === "system") {
      const overrides = await loadSystemRuleOverrides();
      overrides[rule.id] = { ...overrides[rule.id], enabled };
      await saveSystemRuleOverrides(overrides);
    } else {
      const userRules = await loadUserRules();
      const idx = userRules.findIndex((r) => r.id === rule.id);
      if (idx === -1) {
        await rerender();
        return;
      }
      userRules[idx] = { ...userRules[idx], enabled };
      await saveUserRules(userRules);
    }
    log(`${rule.name}: ${enabled ? "enabled" : "disabled"}`);
    await rerender();
  } catch (err) {
    log(`toggle failed: ${errMessage(err)}`, "error");
    await rerender();
  }
}

async function handleEdit(rule: Rule): Promise<void> {
  log(`opening editor for "${rule.name}"…`);
  try {
    const saved = await openRuleEditor(rule);
    if (saved) {
      log(`saved "${saved.name}"`, "success");
      await rerender();
    } else {
      log("edit cancelled");
    }
  } catch (err) {
    log(`edit failed: ${errMessage(err)}`, "error");
  }
}

/**
 * Two-click confirm — window.confirm is unreliable inside Office add-in
 * UIs. First click flips the button to "Confirm?"; second click within
 * 5 seconds performs the delete.
 */
function handleDeleteClick(rule: Rule, btn: HTMLButtonElement): void {
  if (rule.source !== "user") return;
  if (pendingDeleteRuleId === rule.id) {
    pendingDeleteRuleId = null;
    if (pendingDeleteTimer !== null) {
      window.clearTimeout(pendingDeleteTimer);
      pendingDeleteTimer = null;
    }
    btn.classList.remove("rule-action-btn--confirm");
    btn.textContent = "Deleting…";
    btn.disabled = true;
    void handleDeleteConfirmed(rule);
    return;
  }
  document
    .querySelectorAll<HTMLButtonElement>(".rule-action-btn--confirm")
    .forEach((el) => {
      el.classList.remove("rule-action-btn--confirm");
      el.textContent = "Delete";
    });
  pendingDeleteRuleId = rule.id;
  btn.classList.add("rule-action-btn--confirm");
  btn.textContent = "Click again to confirm";
  if (pendingDeleteTimer !== null) window.clearTimeout(pendingDeleteTimer);
  pendingDeleteTimer = window.setTimeout(() => {
    if (pendingDeleteRuleId === rule.id) {
      pendingDeleteRuleId = null;
      btn.classList.remove("rule-action-btn--confirm");
      btn.textContent = "Delete";
    }
    pendingDeleteTimer = null;
  }, 5000);
}

async function handleDeleteConfirmed(rule: Rule): Promise<void> {
  try {
    await deleteUserRule(rule.id);
    const order = (await loadRuleOrder()).filter((id) => id !== rule.id);
    await saveRuleOrder(order);
    log(`deleted "${rule.name}"`, "success");
    await rerender();
  } catch (err) {
    log(`delete failed: ${errMessage(err)}`, "error");
    await rerender();
  }
}

async function handleNewRule(): Promise<void> {
  log("opening rule editor…");
  try {
    const saved = await openRuleEditor();
    if (saved) {
      const order = await loadRuleOrder();
      if (!order.includes(saved.id)) {
        order.push(saved.id);
        await saveRuleOrder(order);
      }
      log(`saved new rule "${saved.name}"`, "success");
      await rerender();
    } else {
      log("cancelled");
    }
  } catch (err) {
    log(`new rule failed: ${errMessage(err)}`, "error");
  }
}

async function handlePersonaChange(value: string): Promise<void> {
  const newId = value === NO_PERSONA ? null : value;
  try {
    await saveActivePersonaId(newId);
    activePersonaId = newId;
    if (newId) {
      const persona = currentPersonas.find((p) => p.id === newId);
      log(`persona: ${persona?.name ?? newId}`);
    } else {
      log("persona cleared");
    }
    renderRulesList();
  } catch (err) {
    log(`persona change failed: ${errMessage(err)}`, "error");
  }
}

async function handleManagePersonas(): Promise<void> {
  const before = currentPersonas.length;
  log("opening persona manager…");
  try {
    await openPersonaManager();
    await rerender();
    const after = currentPersonas.length;
    const delta = after === before ? "no count change" : `${before} → ${after}`;
    log(`persona manager closed (${after} persona(s) loaded, ${delta})`);
    if (activePersonaId === null) await runAutoDetection();
  } catch (err) {
    log(`persona manager failed: ${errMessage(err)}`, "error");
  }
}

async function handleRun(): Promise<void> {
  if (running) {
    log("already running", "error");
    return;
  }
  const scopeSel = document.getElementById("rules-scope") as HTMLSelectElement | null;
  const scope = (scopeSel?.value ?? "sheet") as Scope;

  const runBtn = document.getElementById("rules-run") as HTMLButtonElement | null;
  running = true;
  if (runBtn) runBtn.disabled = true;

  try {
    const allRules = await loadAllRules();
    const personaIds = activePersonaRuleIdSet();
    const personaName =
      activePersonaId !== null
        ? (currentPersonas.find((p) => p.id === activePersonaId)?.name ?? "(unknown)")
        : null;

    const candidates =
      personaIds === null ? allRules : allRules.filter((r) => personaIds.has(r.id));
    const enabled = candidates.filter((r) => r.enabled);

    const personaSuffix = personaName ? ` (persona: ${personaName})` : "";
    log(`running ${enabled.length} enabled rule(s) on ${scope}${personaSuffix}`);
    if (enabled.length === 0) {
      log("nothing to run");
      return;
    }
    const results = await applyRules(enabled, scope, (msg) => log(msg));
    if (results.length === 0) return;
    let total = 0;
    for (const r of results) {
      log(`${r.ruleName}: matched ${r.cellsMatched}, changed ${r.cellsChanged}`);
      total += r.cellsChanged;
    }
    log(`total cells changed: ${total}`, total > 0 ? "success" : "info");
  } catch (err) {
    log(`run failed: ${errMessage(err)}`, "error");
  } finally {
    running = false;
    if (runBtn) runBtn.disabled = false;
  }
}

async function handleReorder(source: number, target: number): Promise<void> {
  if (source === target || source + 1 === target) return;
  const ids = currentRules.map((r) => r.id);
  const [moved] = ids.splice(source, 1);
  const adjusted = source < target ? target - 1 : target;
  ids.splice(adjusted, 0, moved);
  try {
    await saveRuleOrder(ids);
    log("rule order updated");
    await rerender();
  } catch (err) {
    log(`reorder failed: ${errMessage(err)}`, "error");
  }
}

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
